import { ContainerRow, itemCaption } from './container-items.rules';
import {
  ReceivingLogDetails,
  ReceivingLogEditItem,
  ReceivingLogPrintHistory,
} from '../../../models/logs/ReceivingLogDetails';

/**
 * receiving-log-edit.rules.ts
 *
 * Pure rules for rendering `GET /V1/receiving-log/{logId}/details`
 * (handoff-18 §1) on the log-edit screen. Session 1 scope only: read-side
 * derivations the screen needs to render correctly. Nothing here builds a save
 * payload — that is session 2 (handoff §2, §3, §5).
 *
 * Every rule here exists because handoff-18 §1.1/§1.2 called out a specific,
 * non-obvious way this payload can be misread into a wrong label or a false
 * claim about the data. Read those sections before changing anything below.
 */

/**
 * The quantity preview shown at the top of the edit screen.
 *
 * `quantityDisplay` is derived from the ACTIVE item rows, so a log with none —
 * a legacy log, see §7 — has nothing to derive from and comes back `null`.
 * `log.quantity` still holds the real free text ("3 boxes") in that case.
 * Showing `quantityDisplay` unconditionally blanks the quantity on every
 * legacy log, so this always falls back rather than trusting the derived
 * field alone.
 */
export function quantityPreview(details: Pick<ReceivingLogDetails, 'quantityDisplay' | 'log'>): string {
  if (details.quantityDisplay != null) {
    return details.quantityDisplay;
  }
  return details.log?.quantity ?? '';
}

/** How to render a best-effort sub-fetch array once you also know whether it soft-failed. */
export type ArrayRenderState = 'rows' | 'confirmedEmpty' | 'unknown';

/**
 * Classifies an empty-or-not array from this payload against `errorMessages`.
 *
 * `receipts` and `items` are each best-effort: if a sub-fetch fails the
 * response is still `200`, the array is `[]`, and `errorMessages` is set. So
 * an empty array alone is ambiguous — it means EITHER "this log has none" OR
 * "we could not read them". Checking `errorMessages` first is what tells those
 * two apart; skipping that check is how a screen ends up claiming "no receipts
 * on this log" over data it never actually saw.
 *
 * @param rows The `receipts` or `items` array from the response.
 * @param errorMessages The response's `errorMessages` field, verbatim.
 */
export function arrayRenderState(
  rows: unknown[] | null | undefined,
  errorMessages: string | null,
): ArrayRenderState {
  if (rows && rows.length > 0) {
    return 'rows';
  }
  if (errorMessages) {
    return 'unknown';
  }
  return 'confirmedEmpty';
}

/**
 * The caption for one item row: the same "BOX 2 of 3" the label prints for an
 * active row, but WITHOUT a position for a voided one.
 *
 * A voided row's `typeIndex`/`totalCount` are frozen at the moment it was
 * voided; a renumber only rewrites survivors. So a voided "BOX 2 of 3" can sit
 * next to a surviving row that is now "BOX 2 of 2" — captioning the voided row
 * from its own frozen numbers would show two different rows both claiming to
 * be "BOX 2", one of which is a lie. Only active rows have trustworthy
 * captions; a voided row is identified by type + void attribution instead.
 */
export function editItemCaption(item: ReceivingLogEditItem): string {
  const noun = (item.itemType || 'item').toUpperCase();
  if (item.isVoided) {
    return noun;
  }
  return itemCaption({
    itemType: item.itemType || 'item',
    typeIndex: item.typeIndex,
    typeTotal: item.totalCount,
  });
}

/**
 * The void-attribution line for a voided row — "Voided by {voidedBy} on
 * {voidedDate}", optionally followed by the user's own reason text verbatim.
 * `voidReason` is never invented: a void with no reason renders with no reason
 * clause, not a placeholder like "no reason given".
 *
 * Returns `''` for an active row — callers should gate display on `isVoided`
 * rather than relying on this being blank, but a blank string is a safe
 * default if they don't.
 */
export function voidAttributionLine(item: ReceivingLogEditItem): string {
  if (!item.isVoided) {
    return '';
  }
  const who = item.voidedBy || 'unknown user';
  const when = item.voidedDate || 'an unknown date';
  const base = `Voided by ${who} on ${when}`;
  return item.voidReason ? `${base} — "${item.voidReason}"` : base;
}

/**
 * Seeds the container editor from the response's `containerItems` (already
 * collapsed from ACTIVE items server-side). Session 2 owns turning edits to
 * these rows back into a save payload; this only builds the initial working
 * copy the same shape the rest of the container-editing rules already use.
 *
 * ⚠️ NO CALLER outside its own specs. It fed a "was / now" line in the container
 * card that the compact restyle replaced with the Quantity-field before/after
 * section, and the field holding its output has since been removed. The editor
 * itself seeds from `containerRowsFromItems(activeItems)` instead, which reads the
 * ITEM ROWS rather than the server's collapse of them — the same rows the void plan
 * is made against, so the chips and the plan cannot disagree about what is on the
 * log. Delete this and its specs, or find it a caller; do not read this comment as
 * a claim that the screen uses it.
 */
export function containerEditorSeed(
  details: Pick<ReceivingLogDetails, 'containerItems'>,
): ContainerRow[] {
  return (details.containerItems ?? []).map((item) => ({
    quantity: item.quantity,
    itemType: item.itemType,
  }));
}

/** How much weight a pre-save "labels may need reprinting" warning should carry. */
export type PrintWarningLevel = 'none' | 'printed';

/**
 * Whether the print-history block should say anything at all before a save.
 *
 * `lastPrintedDate` informs the WEIGHT of a warning shown before saving — it
 * is never a branch in the save request itself (there is no request-shape
 * difference to pick, see handoff §3). `null` is the easy case: nothing has
 * ever printed for this log, so there is nothing out there to invalidate and
 * the UI should say nothing. Any non-null date means labels exist in the
 * world; how recent it was is presentation detail for the save flow (session
 * 2/3) to weigh, not something this read-side rule decides.
 */
export function printWarningLevel(
  printHistory: Pick<ReceivingLogPrintHistory, 'lastPrintedDate'> | null | undefined,
): PrintWarningLevel {
  return printHistory?.lastPrintedDate ? 'printed' : 'none';
}
